import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Building2, Loader2, AlertCircle, CheckCircle2,
} from 'lucide-react';

import { cardAcquirerApi }  from '@/api/cardAcquirer.api';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { AppRoutes }        from '@/constants/routes';
import { Spinner }          from '@/components/ui/Spinner';
import { cn }               from '@/utils/cn';

import type { CardAcquirerDto, CreateCardAcquirerRequest } from '@/types/cardReconciliation.types';
import { GlAccountLookup, BankLookup, BankAccountLookup } from '@/components/lookups/EntityLookups';

// ── Field component ───────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
}
const Field = ({ label, required, children, hint }: FieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const inputCls = cn(
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors',
  'placeholder:text-slate-400',
);

// ── Page ──────────────────────────────────────────────────────────────────────

export const CardAcquirerFormPage = () => {
  const { id }        = useParams<{ id: string }>();
  const navigate      = useNavigate();
  const isEdit        = !!id && id !== 'new';

  const { hasPermission } = usePermission();
  const canEdit = hasPermission(isEdit ? Permissions.CARD_ACQUIRER_EDIT : Permissions.CARD_ACQUIRER_CREATE);

  const [loading, setLoading]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);

  // ── Form state ────────────────────────────────────────────────────────────
  const [acquirerCode,               setAcquirerCode]              = useState('');
  const [acquirerName,               setAcquirerName]              = useState('');
  const [mdrRate,                    setMdrRate]                   = useState('2.500');
  const [settlementDelayDays,        setSettlementDelayDays]       = useState('3');
  const [isActive,                   setIsActive]                  = useState(true);
  const [remarks,                    setRemarks]                   = useState('');
  // Where the acquirer pays the money: the settlement import books the bank leg to this account.
  const [bankId,                     setBankId]                    = useState('');
  const [settlementBankAccountId,    setSettlementBankAccountId]   = useState('');

  // GL account IDs (optional — free text GUIDs for now; real impl would use lookup)
  const [cardClearingGLAccountId,    setCardClearingGLAccountId]   = useState('');
  const [cardChargesExpenseAccountId,setCardChargesExpenseAccountId] = useState('');
  const [chargebackAccountId,        setChargebackAccountId]       = useState('');
  const [varianceAccountId,          setVarianceAccountId]         = useState('');

  // ── Load existing record ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    cardAcquirerApi.getById(id!)
      .then(res => {
        const a = res.data?.data as CardAcquirerDto | undefined;
        if (!a) { setError('Acquirer not found.'); return; }
        setAcquirerCode(a.acquirerCode);
        setAcquirerName(a.acquirerName);
        setMdrRate((a.mdrRate * 100).toFixed(3));
        setSettlementDelayDays(String(a.settlementDelayDays));
        setIsActive(a.isActive);
        setRemarks(a.remarks ?? '');
        setBankId(a.bankId ?? '');
        setSettlementBankAccountId(a.settlementBankAccountId ?? '');
        setCardClearingGLAccountId(a.cardClearingGLAccountId ?? '');
        setCardChargesExpenseAccountId(a.cardChargesExpenseAccountId ?? '');
        setChargebackAccountId(a.chargebackAccountId ?? '');
        setVarianceAccountId(a.varianceAccountId ?? '');
      })
      .catch(() => setError('Failed to load acquirer.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!acquirerCode.trim() || !acquirerName.trim()) {
      setError('Acquirer code and name are required.');
      return;
    }
    const mdr = parseFloat(mdrRate);
    if (isNaN(mdr) || mdr < 0 || mdr > 100) {
      setError('MDR Rate must be between 0 and 100.');
      return;
    }
    const days = parseInt(settlementDelayDays);
    if (isNaN(days) || days < 0) {
      setError('Settlement delay days must be a non-negative integer.');
      return;
    }

    setSaving(true);
    setError(null);

    const toGuid = (v: string) => v.trim() || null;
    const mdrDecimal = mdr / 100;

    try {
      if (isEdit) {
        await cardAcquirerApi.update(id!, {
          acquirerName:               acquirerName.trim(),
          mdrRate:                    mdrDecimal,
          settlementDelayDays:        days,
          isActive,
          bankId:                     toGuid(bankId),
          settlementBankAccountId:    toGuid(settlementBankAccountId),
          cardClearingGLAccountId:    toGuid(cardClearingGLAccountId),
          cardChargesExpenseAccountId: toGuid(cardChargesExpenseAccountId),
          chargebackAccountId:        toGuid(chargebackAccountId),
          varianceAccountId:          toGuid(varianceAccountId),
          remarks:                    remarks.trim() || null,
        });
        setSuccess(true);
        setTimeout(() => navigate(AppRoutes.CARD_ACQUIRERS), 1200);
      } else {
        const req: CreateCardAcquirerRequest = {
          acquirerCode:               acquirerCode.trim().toUpperCase(),
          acquirerName:               acquirerName.trim(),
          mdrRate:                    mdrDecimal,
          settlementDelayDays:        days,
          bankId:                     toGuid(bankId),
          settlementBankAccountId:    toGuid(settlementBankAccountId),
          cardClearingGLAccountId:    toGuid(cardClearingGLAccountId),
          cardChargesExpenseAccountId: toGuid(cardChargesExpenseAccountId),
          chargebackAccountId:        toGuid(chargebackAccountId),
          varianceAccountId:          toGuid(varianceAccountId),
          remarks:                    remarks.trim() || null,
        };
        await cardAcquirerApi.create(req);
        setSuccess(true);
        setTimeout(() => navigate(AppRoutes.CARD_ACQUIRERS), 1200);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Failed to save acquirer.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  if (error && isEdit && !acquirerName) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 dark:text-gray-400 gap-3">
        <AlertCircle size={40} className="text-red-400" />
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="text-teal-600 dark:text-teal-400 hover:underline text-sm">Go back</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.CARD_ACQUIRERS)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              <Building2 size={20} className="text-teal-600 dark:text-teal-400" />
              <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {isEdit ? `Edit Acquirer — ${acquirerCode}` : 'New Card Acquirer'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {isEdit ? 'Update acquiring bank configuration' : 'Register a new acquiring bank or payment network'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(AppRoutes.CARD_ACQUIRERS)}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={saving || success}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  success
                    ? 'bg-green-600 text-white'
                    : 'bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50',
                )}
              >
                {saving  ? <Loader2 size={15} className="animate-spin" /> :
                 success ? <CheckCircle2 size={15} /> :
                           <Save size={15} />}
                {saving ? 'Saving…' : success ? 'Saved!' : 'Save Acquirer'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Error banner */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
            </div>
          )}

          {/* ── Identity ─────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Acquirer Identity
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Acquirer Code" required>
                <input
                  value={acquirerCode}
                  onChange={e => setAcquirerCode(e.target.value.toUpperCase())}
                  disabled={isEdit}
                  placeholder="e.g. VISA-CB, MC-HNB"
                  className={cn(inputCls, isEdit && 'bg-slate-50 dark:bg-slate-600/50 cursor-not-allowed opacity-70')}
                />
              </Field>
              <Field label="Acquirer Name" required>
                <input
                  value={acquirerName}
                  onChange={e => setAcquirerName(e.target.value)}
                  placeholder="e.g. Visa via Commercial Bank"
                  className={inputCls}
                />
              </Field>
            </div>

            {isEdit && (
              <div className="mt-4 flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                    className="rounded border-slate-300 dark:border-gray-600 text-teal-600 dark:text-teal-400 focus:ring-teal-500"
                  />
                  <span>Active</span>
                </label>
              </div>
            )}
          </div>

          {/* ── Settlement terms ─────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Settlement Terms
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="MDR Rate (%)" required hint="Merchant Discount Rate — e.g. 2.500 for 2.5%">
                <div className="relative">
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    max="100"
                    value={mdrRate}
                    onChange={e => setMdrRate(e.target.value)}
                    className={cn(inputCls, 'pr-8')}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                </div>
              </Field>
              <Field label="Settlement Delay (days)" required hint="Expected days from sale to bank settlement">
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={settlementDelayDays}
                  onChange={e => setSettlementDelayDays(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Settlement Bank" hint="The bank the acquirer pays into">
                <BankLookup
                  value={bankId}
                  onChange={v => { const next = v ?? ''; if (next !== bankId) setSettlementBankAccountId(''); setBankId(next); }}
                />
              </Field>
              <Field label="Settlement Bank Account" hint="Settlement imports book the money received to this account">
                <BankAccountLookup
                  bankId={bankId || null}
                  value={settlementBankAccountId}
                  onChange={v => setSettlementBankAccountId(v ?? '')}
                />
              </Field>
            </div>
          </div>

          {/* ── GL Accounts ──────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1 uppercase tracking-wide">
              GL Account Mapping
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              The accounts a settlement import and a reconciliation post to for this acquirer. Any left
              empty use the company account set in Posting Accounts.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Card Clearing / Receivable Account" hint="Card receivable clearing account">
                <GlAccountLookup
                  value={cardClearingGLAccountId}
                  onChange={v => setCardClearingGLAccountId(v ?? '')}
                />
              </Field>
              <Field label="MDR / Commission Expense Account" hint="MDR charges expense account">
                <GlAccountLookup
                  value={cardChargesExpenseAccountId}
                  onChange={v => setCardChargesExpenseAccountId(v ?? '')}
                />
              </Field>
              <Field label="Chargeback Account" hint="Chargeback expense / receivable">
                <GlAccountLookup
                  value={chargebackAccountId}
                  onChange={v => setChargebackAccountId(v ?? '')}
                />
              </Field>
              <Field label="Variance Account" hint="Settlement variance account">
                <GlAccountLookup
                  value={varianceAccountId}
                  onChange={v => setVarianceAccountId(v ?? '')}
                />
              </Field>
            </div>
          </div>

          {/* ── Remarks ──────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Remarks
            </h2>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              rows={3}
              placeholder="Optional notes about this acquirer…"
              className={cn(inputCls, 'resize-none')}
            />
          </div>

        </div>
      </div>
    </div>
  );
};
